import { useCallback, useEffect } from "react";

const useFilePanelHandlers = ({
  fileContentExpanded,
  setFileContentExpanded,
  setActiveFile,
  setActiveFileContent,
  setChatPanelWidth,
  filesRef,
  inputRef,
  setSelectedExpert,
  setIsInExpertMode,
  setCurrentExpertCommand,
  isMainDragging,
  setIsMainDragging,
  setIsChatPanelVisible,
  fileStyles,
}) => {
  const handleCloseFile = useCallback(() => {
    setActiveFile(null);
    setFileContentExpanded(false);

    const containerWidth =
      document.querySelector(".chatAndFileContainer")?.clientWidth || 1000;
    const fileListWidthPercent = (220 / containerWidth) * 100;
    const newChatPanelWidth = Math.min(100 - fileListWidthPercent, 85);
    setChatPanelWidth(newChatPanelWidth);
  }, [setActiveFile, setChatPanelWidth, setFileContentExpanded]);

  const handleExpandFileContent = useCallback(() => {
    setFileContentExpanded(true);
    setChatPanelWidth(38.2);
  }, [setChatPanelWidth, setFileContentExpanded]);

  const handleFileCardClick = useCallback(
    (fileId) => {
      if (window.mdxContentSyncTimeout) {
        clearTimeout(window.mdxContentSyncTimeout);
        if (window.saveCurrentFileContent) {
          window.saveCurrentFileContent();
        }
      }

      setActiveFileContent(null);

      const currentFiles = filesRef.current;
      if (currentFiles[fileId]) {
        console.log("🔍 点击文件:", fileId);
        console.log(
          "🔍 文件内容预览:",
          currentFiles[fileId].content?.substring(0, 100)
        );
        console.log("🔍 文件 meta.filename:", currentFiles[fileId].meta?.filename);

        setActiveFile(fileId);

        setTimeout(() => {
          const latestFiles = filesRef.current;
          if (latestFiles[fileId]) {
            setActiveFileContent({
              fileName: fileId,
              content: latestFiles[fileId].content,
            });
          }
        }, 0);
      } else {
        console.error("文件不存在:", fileId);
        return;
      }

      if (!fileContentExpanded) {
        setFileContentExpanded(true);
        setChatPanelWidth(38.2);
      }
    },
    [
      fileContentExpanded,
      filesRef,
      setActiveFile,
      setActiveFileContent,
      setChatPanelWidth,
      setFileContentExpanded,
    ]
  );

  const handleExpertFileDoubleClick = useCallback(
    (fileId) => {
      const fileData = filesRef.current[fileId];
      if (!fileData?.meta?.isExpertDialog) return;

      const expertName = fileData.meta.expert_name;
      const expertCmd = fileData.meta.expert_cmd;
      if (!expertName) return;

      const expertPrefix = `@${expertName} `;

      setSelectedExpert({
        comd: expertCmd || "",
        display_name: expertName,
      });
      setIsInExpertMode(true);
      setCurrentExpertCommand(`@${expertCmd || expertName}`);

      setTimeout(() => {
        const inputElement =
          inputRef.current || document.querySelector('textarea[name="humanInput"]');
        if (inputElement) {
          inputElement.value = expertPrefix;
          inputElement.focus();
          inputElement.setSelectionRange(expertPrefix.length, expertPrefix.length);
          const inputEvent = new Event("input", { bubbles: true });
          inputElement.dispatchEvent(inputEvent);
        }
      }, 100);
    },
    [
      filesRef,
      inputRef,
      setCurrentExpertCommand,
      setIsInExpertMode,
      setSelectedExpert,
    ]
  );

  const toggleChatPanel = useCallback(() => {
    setIsChatPanelVisible((prev) => !prev);
  }, [setIsChatPanelVisible]);

  const handleMainDragStart = useCallback(
    (e) => {
      e.preventDefault();
      setIsMainDragging(true);
      document.body.classList.add(fileStyles.mainDragging);
    },
    [fileStyles.mainDragging, setIsMainDragging]
  );

  useEffect(() => {
    const initMainPanelResize = () => {
      const handleMainDragMove = (e) => {
        if (!isMainDragging) return;
        const containerWidth = window.innerWidth;
        const newChatPanelWidth = (e.clientX / containerWidth) * 100;
        const limitedWidth = Math.min(Math.max(newChatPanelWidth, 20), 80);
        setChatPanelWidth(limitedWidth);
      };

      const handleMainDragEnd = () => {
        setIsMainDragging(false);
        document.body.classList.remove(fileStyles.mainDragging);
      };

      // 🚀 使用 passive: true 提升拖拽性能
      document.addEventListener("mousemove", handleMainDragMove, { passive: true });
      document.addEventListener("mouseup", handleMainDragEnd, { passive: true });

      return () => {
        document.removeEventListener("mousemove", handleMainDragMove);
        document.removeEventListener("mouseup", handleMainDragEnd);
      };
    };

    const cleanup = initMainPanelResize();
    return cleanup;
  }, [fileStyles.mainDragging, isMainDragging, setChatPanelWidth, setIsMainDragging]);

  return {
    handleCloseFile,
    handleExpandFileContent,
    handleFileCardClick,
    handleExpertFileDoubleClick,
    toggleChatPanel,
    handleMainDragStart,
  };
};

export default useFilePanelHandlers;
